import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import TeamService from "../services/teamService";
import {
  Loader2,
  Lock,
  Eye,
  EyeOff,
  CheckCircle2,
  AlertCircle,
  XCircle,
  ArrowLeft,
} from "lucide-react";

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get token from URL
  const token = searchParams.get("token");
  
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [userName, setUserName] = useState("");

  useEffect(() => {
    // Verify token when component mounts
    const verifyToken = async () => {
      //console.log("Verifying token:", token);
      
      if (!token) {
        setError("Invalid or missing reset token. Please use the link from your email.");
        setVerifying(false);
        return;
      }

      try {
        const response = await TeamService.verifyResetToken(token);
       // console.log("Verify response:", response);
        
        if (response.success) {
          setTokenValid(true);
          // Adjust based on your API response structure
          setUserEmail(response.data?.email || "");
          setUserName(response.data?.name || "");
        } else {
          setError(response.message || "Invalid or expired reset token");
        }
      } catch (err) {
        console.error("Error verifying token:", err);
        setError(
          err.message ||
          "Invalid or expired reset token. Please request a new reset link."
        );
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

const handlePasswordReset = async (e) => {
  e.preventDefault();
  setMessage(null);
  setError(null);

  // Validate passwords
  if (!newPassword || !confirmPassword) {
    setError("Please fill in all fields");
    return;
  }

  if (newPassword !== confirmPassword) {
    setError("Passwords do not match");
    return;
  }

  if (newPassword.length < 8) {
    setError("Password must be at least 8 characters long");
    return;
  }

  try {
    setIsLoading(true);
    
    const response = await TeamService.resetPasswordWithToken({
      token,
      newPassword,
    });
    
    //console.log("Reset response:", response);
    
    if (response.success) {
      setMessage("Password reset successfully! You have been logged out. Redirecting to login...");
      
      // ✅ Store the email BEFORE clearing localStorage (optional but good practice)
      const userEmailForRedirect = userEmail;
      
      // ✅ Get the token BEFORE clearing it
      const currentToken = TeamService.getAuthToken();
      
      // ✅ CRITICAL: Force logout by clearing ALL authentication data
      // Clear all localStorage items
      localStorage.clear();
      
      // Clear any sessionStorage items
      sessionStorage.clear();
      
      // Clear TeamService tokens (though localStorage.clear() already did this)
      TeamService.removeAuthToken();
      TeamService.removeAuthUser();
      
      // Try to call logout API if there was a valid token
      try {
        if (currentToken) {
          // Create a temporary token for logout API call
          // Note: This might fail since password was just changed, but we try anyway
          await TeamService.logout();
        }
      } catch (logoutErr) {
        console.log("Logout API call optional, auth already cleared:", logoutErr.message);
        // Ignore errors - we've already cleared local storage
      }
      
      // Redirect to SignUp page (login tab) with email pre-filled
       setTimeout(() => {
    // Use window.location.replace for complete navigation
    window.location.replace("/"); // This will redirect to home page
  }, 2000);
    } else {
      setError(response.message || "Failed to reset password");
    }
  } catch (err) {
    console.error("Reset password error:", err);
    setError(
      err.message ||
      err.response?.data?.message ||
        "Failed to reset password. The link may have expired. Please request a new reset link."
    );
  } finally {
    setIsLoading(false);
  }
};

  const handleBackToLogin = () => {
    // Clear any existing auth and redirect to signup (login page)
    localStorage.clear();
    TeamService.removeAuthToken();
    TeamService.removeAuthUser();
    navigate("/");
  };

  const handleRequestNewLink = () => {
    navigate("/");
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full text-center">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <Loader2 className="h-12 w-12 text-blue-600 animate-spin mx-auto mb-6" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Verifying Reset Link</h2>
            <p className="text-gray-600">Please wait while we verify your reset link...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Invalid Reset Link</h2>
            <p className="text-gray-600 mb-6">
              {error || "This password reset link is invalid or has expired."}
            </p>
            <div className="space-y-3">
              <button
                onClick={handleRequestNewLink}
                className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                Request New Reset Link
              </button>
              <button
                onClick={handleBackToLogin}
                className="w-full bg-gray-100 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Back to Login
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center px-4 py-8">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="flex justify-center mb-8">
          <img
            src="/images/FibreLogo.jpg"
            alt="Company Logo"
            className="h-16 w-auto"
          />
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl mr-4">
                  <Lock className="h-7 w-7 text-white" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">Reset Password</h2>
                  {userEmail && (
                    <p className="text-blue-100 text-sm mt-1">for {userEmail}</p>
                  )}
                </div>
              </div>
              <button
                onClick={handleBackToLogin}
                className="text-white hover:text-blue-100 transition-colors"
                title="Back to login"
              >
                <ArrowLeft className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handlePasswordReset} className="p-8 space-y-6">
            {/* Welcome Message */}
            <div className="text-center mb-4">
              <p className="text-gray-700">
                Hello <span className="font-semibold">{userName || "User"}</span>,
              </p>
              <p className="text-gray-600 text-sm mt-1">
                Please enter your new password below.
              </p>
            </div>

            {/* New Password */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Enter new password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showNewPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Confirm New Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                  placeholder="Confirm new password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>

            {/* Password Requirements */}
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <p className="text-xs font-medium text-gray-600 mb-2">Password must:</p>
              <ul className="space-y-1">
                <li className={`text-xs flex items-center ${newPassword.length >= 8 ? 'text-green-600' : 'text-gray-500'}`}>
                  <div className={`w-1.5 h-1.5 rounded-full mr-2 ${newPassword.length >= 8 ? 'bg-green-600' : 'bg-gray-400'}`}></div>
                  Be at least 8 characters long
                </li>
                <li className={`text-xs flex items-center ${newPassword && confirmPassword && newPassword === confirmPassword ? 'text-green-600' : 'text-gray-500'}`}>
                  <div className={`w-1.5 h-1.5 rounded-full mr-2 ${newPassword && confirmPassword && newPassword === confirmPassword ? 'bg-green-600' : 'bg-gray-400'}`}></div>
                  Match in both fields
                </li>
              </ul>
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-start bg-red-50 border border-red-200 rounded-xl p-4">
                <AlertCircle className="h-5 w-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800 font-medium">{error}</p>
              </div>
            )}

            {/* Success Message */}
            {message && (
              <div className="flex items-start bg-green-50 border border-green-200 rounded-xl p-4">
                <CheckCircle2 className="h-5 w-5 text-green-600 mr-3 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-green-800 font-medium">{message}</p>
                  <p className="text-xs text-green-600 mt-1">
                    You will be redirected to login page where you can use your new password.
                  </p>
                  <p className="text-xs text-green-600 mt-1">
                    <strong>Note:</strong> All active sessions have been logged out for security.
                  </p>
                </div>
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || !tokenValid}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3.5 rounded-xl font-semibold hover:from-blue-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isLoading ? (
                <>
                  <Loader2 className="animate-spin h-5 w-5 mr-2" />
                  Resetting Password...
                </>
              ) : (
                <>
                  <Lock className="h-5 w-5 mr-2" />
                  Reset Password & Logout
                </>
              )}
            </button>

            {/* Security Notice */}
            <div className="text-center pt-4 border-t border-gray-100">
              <p className="text-xs text-gray-500">
                <strong>Security Notice:</strong> After resetting your password, all active sessions will be terminated and you'll need to login again with your new password.
              </p>
            </div>
          </form>
        </div>

        {/* Security Info */}
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            For security, this link expires in 1 hour
          </p>
          <p className="text-xs text-gray-400 mt-1">
            After resetting, you'll be redirected to the login page
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;